/** Erreur d'API portant le statut HTTP, pour distinguer 401 (déconnexion) du reste. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Client HTTP unique. `credentials: 'include'` est indispensable : la session
 * vit dans un cookie httpOnly, jamais dans le localStorage (inaccessible au JS,
 * donc involable par XSS).
 */
export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  if (!res.ok) {
    let message = `Erreur ${res.status}`;
    try {
      const body = await res.json();
      if (typeof body?.error === 'string') message = body.error;
    } catch {
      // Réponse non-JSON (proxy, coupure réseau) — on garde le message générique.
    }
    throw new ApiError(res.status, message);
  }

  return res.json() as Promise<T>;
}

// --- Types partagés avec l'API --------------------------------------------

export type SessionUser = {
  id: number;
  username: string;
  fullName: string;
  role: 'REP' | 'ADMIN';
};


export type MeResponse = { user: SessionUser };

export type Customer = {
  id: number;
  custNum: string;
  name: string;
  address: string | null;
  city: string | null;
  province: string | null;
  postalCode: string | null;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  salespersonNumber: string | null;
  salespersonName: string | null;
  /** N° client Epicor (une fiche par client + job). */
  clientNum?: string | null;
  job?: number;
  altIds?: string[];
  /** 'S' = compte suspendu ; 'N' et null sont commandables. */
  chargeAllowed?: 'Y' | 'N' | 'S' | null;
  accountNumber?: string | null;
};

/** Ligne de la liste : seule la route de liste calcule ces champs. */
export type CustomerListRow = Customer & { suspended: boolean; jobCount: number };

/** Seul chargeAllowed = 'S' bloque la commande (les fiches détail/vérifier n'ont pas « suspended »). */
export const isSuspendedJob = (c: Pick<Customer, 'chargeAllowed'>) => c.chargeAllowed === 'S';

export type CustomerDetail = { customer: Customer; emails: unknown[]; jobs: Customer[] };

export type VerifyResult = { customer: Customer; jobs: Customer[]; checked: boolean };

export type CustomerScope = 'mine' | 'all';

export type CustomerSearch = {
  customers: CustomerListRow[];
  total: number;
  limit: number;
  offset: number;
  scope: CustomerScope;
  /** N° vendeur ERP du compte connecté. */
  myNumbers: string[];
};

/** N° vendeur présent dans le carnet d'adresses. */
export type Salesperson = { number: string; name: string | null; customers: number };

export type HouseSettings = { houseNumbers: string[]; houseVisibleToReps: boolean };

export type CustomersStatus = { ficheClientConfigured: boolean; active: number; lastUpdate: string | null };

export type SyncResult = {
  imported: number;
  jobs: number;
  suspended: number;
  attached: number;
  deactivated: number;
  dryRun: boolean;
};

// --- Catalogue et commandes (« quotes » côté API) ---------------------------

export type CustomerPrice = {
  priced: boolean;
  unitPrice: string | null;
  listPrice: string | null;
  coreCost: string;
  stock: number | null;
};

export type CatalogItem = {
  source: string;
  partNumber: string;
  title: string;
  brand: string | null;
  partType: string | null;
  image: string | null;
  epicor: { partNum: string; mfgCode: string | null; mfgCode2: string | null };
  price: CustomerPrice | null;
};

export type CatalogSearch = {
  mode: { catalogue: 'mock' | 'api'; pricing: 'mock' | 'epicor' };
  items: CatalogItem[];
  barcodeUnavailable?: boolean;
};

export type QuoteStatus = 'DRAFT' | 'SENT' | 'ACCEPTED' | 'CANCELLED';
export type PriceSource = 'epicor' | 'special_quote';

export type QuoteLine = {
  id: number;
  partNum: string;
  mfgCode: string;
  title: string;
  brand: string | null;
  qty: number;
  epicorPrice: string | null;
  listPrice: string | null;
  unitPrice: string;
  priceSource: PriceSource;
  coreCost: string;
};

export type QuotePayload = {
  quote: {
    id: number;
    number: string;
    status: QuoteStatus;
    note: string | null;
    createdAt: string;
    updatedAt: string;
    repName: string;
  };
  customer: Customer;
  lines: QuoteLine[];
  totals: { subtotal: string; cores: string; total: string; itemCount: number };
  /** Présent à l'ouverture d'un brouillon : vrai si Epicor n'a pas pu retarifer les lignes (valeurs gardées). */
  pricesStale?: boolean;
};

export type QuoteSummary = {
  id: number;
  number: string;
  status: QuoteStatus;
  updatedAt: string;
  custNum: string;
  /** Job du client (0 = compte de base). */
  job: number;
  customerName: string;
  repName: string;
  lineCount: number;
  total: string;
};

export type QuoteCosts = {
  lines: { id: number; cost: string | null; marginPct: number | null; belowCost: boolean }[];
  totals: { cost: string; margin: string; marginPct: number | null; complete: boolean };
  /** Vrai si l'API Pièce n'a pas répondu pour des lignes sans coûtant : réessayer. */
  unavailable: boolean;
};

export type Health = { catalogueMode: 'mock' | 'api'; tomcat: 'mock' | 'configured' };

export type RepAccount = {
  id: number;
  username: string;
  fullName: string;
  email: string | null;
  role: 'REP' | 'ADMIN';
  isActive: boolean;
  /** Faux : créé sans mot de passe, ne peut pas se connecter. */
  hasPassword: boolean;
  salespersonNumbers: string[];
  lastLoginAt: string | null;
  createdAt: string;
};

export const STATUS_LABEL: Record<QuoteStatus, string> = {
  DRAFT: 'Brouillon',
  SENT: 'Transmise',
  ACCEPTED: 'Acceptée',
  CANCELLED: 'Annulée',
};
